import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema } from './common.js';

/**
 * Website-manager dashboard drill-down - what sits *behind* each tile count
 * in `website-health`: a destination-wise matrix (packages / destination
 * pages / blogs per destination) plus a per-surface split.
 *
 * Read-only and scoped to the two website roles; see
 * `GET /dashboard/website-breakdown`.
 */

const Count = z.number().int().nonnegative();

/**
 * Same three numbers the tiles show. `total` also carries the statuses that
 * are neither draft nor live (ARCHIVED, INACTIVE, PENDING_REVIEW), so
 * `live + draft` can be less than `total`.
 */
export const WebsiteBreakdownCountsSchema = z.object({
  total: Count,
  /** PUBLISHED, or ACTIVE for packages. */
  live: Count,
  draft: Count,
});
export type WebsiteBreakdownCounts = z.infer<typeof WebsiteBreakdownCountsSchema>;

/** One labelled slice of a surface (a category, a page type, a scope...). */
export const WebsiteBreakdownBucketSchema = WebsiteBreakdownCountsSchema.extend({
  key: z.string(),
  label: z.string(),
});
export type WebsiteBreakdownBucket = z.infer<typeof WebsiteBreakdownBucketSchema>;

/** Raw status tally - one row per status value present on the surface. */
export const WebsiteBreakdownStatusSchema = z.object({
  status: z.string(),
  count: Count,
});
export type WebsiteBreakdownStatus = z.infer<typeof WebsiteBreakdownStatusSchema>;

export const WebsiteDestinationBreakdownRowSchema = z.object({
  /** Destination master id. `null` is the single "not linked" catch-all row. */
  destinationId: UuidSchema.nullable(),
  name: z.string(),
  /** `null` on the catch-all row, where there is no destination to ask. */
  isInternational: z.boolean().nullable(),
  packages: WebsiteBreakdownCountsSchema,
  destinationPages: WebsiteBreakdownCountsSchema,
  blogs: WebsiteBreakdownCountsSchema,
});
export type WebsiteDestinationBreakdownRow = z.infer<typeof WebsiteDestinationBreakdownRowSchema>;

export const WebsiteBlogCategoryBreakdownRowSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  isActive: z.boolean(),
  posts: WebsiteBreakdownCountsSchema,
});
export type WebsiteBlogCategoryBreakdownRow = z.infer<typeof WebsiteBlogCategoryBreakdownRowSchema>;

export const WebsiteCategoryPageBreakdownRowSchema = z.object({
  id: UuidSchema,
  title: z.string(),
  slug: z.string(),
  status: z.string(),
  /** Destination pages listed under this category page. */
  destinationPages: Count,
  /** Packages pinned as best sellers on this category page. */
  bestSellers: Count,
});
export type WebsiteCategoryPageBreakdownRow = z.infer<typeof WebsiteCategoryPageBreakdownRowSchema>;

/**
 * What a destination page needs before it looks finished on the site. The
 * dashboard's "Page completeness" table grades every page against these.
 */
export const DESTINATION_PAGE_COMPLETENESS_RULES = {
  /** "…, But Make It Reel" strip. */
  minReels: 5,
  /** Package sliders (Group Escapes, Honeymoon Escapes, Family Favourites…). */
  minSections: 3,
  /** Cards each slider shows - fewer and the rail looks half-empty. */
  minCardsPerSection: 3,
} as const;

/**
 * A destination page with no sliders configured builds its package sections
 * itself, from this many of the destination's newest active packages grouped
 * by collection. Shared so the site and the dashboard's completeness count
 * always look at the same window.
 */
export const DESTINATION_AUTO_SECTION_PACKAGE_LIMIT = 24;

export const WebsiteDestinationPageSectionFillSchema = z.object({
  heading: z.string(),
  /** Cards the site renders in this section. */
  cards: Count,
});
export type WebsiteDestinationPageSectionFill = z.infer<
  typeof WebsiteDestinationPageSectionFillSchema
>;

export const WebsiteDestinationPageCompletenessRowSchema = z.object({
  id: UuidSchema,
  title: z.string(),
  status: z.string(),
  /** Hero image or hero video is set. */
  hasBanner: z.boolean(),
  reels: Count,
  /**
   * Where the page's package sections come from on the site:
   * `cms` - sliders configured on the page (only those show, in their order);
   * `auto` - none configured, so the site groups the destination's newest
   *   packages by collection;
   * `none` - no destination linked, so there is no package area at all.
   */
  sectionsSource: z.enum(['cms', 'auto', 'none']),
  sections: z.array(WebsiteDestinationPageSectionFillSchema),
});
export type WebsiteDestinationPageCompletenessRow = z.infer<
  typeof WebsiteDestinationPageCompletenessRowSchema
>;

/** One package, graded against the package completeness rules. */
export const WebsitePackageCompletenessRowSchema = z.object({
  id: UuidSchema,
  title: z.string(),
  status: z.string(),
  /** A main / banner image or banner video is set. */
  hasBanner: z.boolean(),
  /** Gallery image count. */
  images: Count,
  /** Overview (rich text) is filled in. */
  hasOverview: z.boolean(),
  /** Itinerary day rows actually created. */
  itineraryDays: Count,
  /** Expected number of days (from the package). */
  numberOfDays: Count,
  /** Inclusion line count. */
  inclusions: Count,
  /** Both meta title and meta description are set. */
  hasSeo: z.boolean(),
});
export type WebsitePackageCompletenessRow = z.infer<typeof WebsitePackageCompletenessRowSchema>;

/** Landing-page (/lp/<slug>) completeness: banner + custom packages + reviews. */
export const WEBSITE_LANDING_PAGE_COMPLETENESS_RULES = {
  minPackages: 3,
  minReviews: 3,
} as const;

export const WebsiteLandingPageCompletenessRowSchema = z.object({
  id: UuidSchema,
  title: z.string(),
  slug: z.string(),
  status: z.string(),
  /** A HERO section (or page hero media) is set. */
  hasBanner: z.boolean(),
  /** Custom-package cards across LANDING_PACKAGES sections. */
  packages: Count,
  /** Reviews across CLIENT_STORIES / REVIEWS sections. */
  reviews: Count,
});
export type WebsiteLandingPageCompletenessRow = z.infer<
  typeof WebsiteLandingPageCompletenessRowSchema
>;

export const WebsiteBreakdownResponseSchema = z.object({
  /** Busiest destination first; the "not linked" row, when present, is last. */
  destinations: z.array(WebsiteDestinationBreakdownRowSchema),
  packages: z.object({
    byStatus: z.array(WebsiteBreakdownStatusSchema),
    byScope: z.array(WebsiteBreakdownBucketSchema),
    /**
     * A package can carry several categories, and is counted under each -
     * so these rows add up to more than the package total.
     */
    byCategory: z.array(WebsiteBreakdownBucketSchema),
    byTripType: z.array(WebsiteBreakdownBucketSchema),
  }),
  blogs: z.object({
    byStatus: z.array(WebsiteBreakdownStatusSchema),
    byCategory: z.array(WebsiteBreakdownBucketSchema),
  }),
  blogCategories: z.array(WebsiteBlogCategoryBreakdownRowSchema),
  pages: z.object({
    byStatus: z.array(WebsiteBreakdownStatusSchema),
    byType: z.array(WebsiteBreakdownBucketSchema),
  }),
  destinationPages: z.object({
    byStatus: z.array(WebsiteBreakdownStatusSchema),
    byScope: z.array(WebsiteBreakdownBucketSchema),
  }),
  categoryPages: z.object({
    byStatus: z.array(WebsiteBreakdownStatusSchema),
    rows: z.array(WebsiteCategoryPageBreakdownRowSchema),
  }),
  /** Every destination page, graded against `DESTINATION_PAGE_COMPLETENESS_RULES`. */
  destinationPageCompleteness: z.array(WebsiteDestinationPageCompletenessRowSchema),
  /** Every active package, graded against the package completeness rules. */
  packageCompleteness: z.array(WebsitePackageCompletenessRowSchema),
  /** Every landing page (/lp/<slug>), graded for banner + packages + reviews. */
  landingPageCompleteness: z.array(WebsiteLandingPageCompletenessRowSchema),
  generatedAt: IsoDateTimeSchema,
});
export type WebsiteBreakdownResponse = z.infer<typeof WebsiteBreakdownResponseSchema>;
